import type { MailBrand, MailSeries, MailTemplate, MailTextBlock, MailVariable, Segment, Subscriber } from '../../core/types/mail'

/**
 * DEMO — voorbeelddata voor Mail & nieuwsbrief: de automatische mails (zoals ze in Laravel staan),
 * de reeksen, de huisstijl, abonnees en doelgroepen. De server levert dit via GET /admin/mails,
 * /admin/subscribers en /admin/segments.
 */

const v = (key: string, label: string, example: string): MailVariable => ({ key, label, example })
const voornaam = v('voornaam', 'Voornaam van de klant', 'Eva')
const bestelnummer = v('bestelnummer', 'Bestelnummer', 'BB-1042')
const bestellink = v('bestellink', 'Link naar de bestelling in Mijn omgeving', 'https://beautybewust.nl/account/bestellingen')
const volglink = v('volglink', 'Track & trace-link van de bezorger', 'https://jouw.postnl.nl/track-and-trace/3SABCD1234567')
const datum = v('datum', 'Datum van de afspraak, bijv. "dinsdag 14 oktober"', 'dinsdag 14 oktober')
const tijd = v('tijd', 'Begintijd van de afspraak', '10:30')
const behandeling = v('behandeling', 'Naam van de behandeling', 'Huidanalyse + gezichtsbehandeling')
const behandelaar = v('behandelaar', 'Wie de behandeling doet (gedeelde salon)', 'Mila')
const afspraaklink = v('afspraaklink', 'Link om de afspraak te verzetten of te annuleren', 'https://beautybewust.nl/account/afspraken')
const bedrag = v('bedrag', 'Bedrag, met euroteken', '€ 50,00')
const code = v('code', 'Code van de cadeaubon', 'BB-7KQM-2XPA-9DWR')
const geldigtot = v('geldigtot', 'Einddatum van de cadeaubon', '4 oktober 2028')
const product = v('product', 'Naam van het product', 'Sensibio Defensive serum')
const leverdatum = v('leverdatum', 'Datum van de volgende levering', '18 oktober')
const abonnementlink = v('abonnementlink', 'Link naar het abonnement in Mijn omgeving', 'https://beautybewust.nl/account/thuis#levering')
const inloglink = v('inloglink', 'Eenmalige inloglink (15 minuten geldig)', 'https://beautybewust.nl/account?inloglink=…')
const herstellink = v('herstellink', 'Link om een nieuw wachtwoord te kiezen', 'https://beautybewust.nl/account?herstel=…')
const winkellink = v('winkellink', 'Link naar de webshop', 'https://beautybewust.nl/webshop')
const mandlink = v('mandlink', 'Link die de winkelmand terugzet', 'https://beautybewust.nl/webshop?mand=…')
const reviewlink = v('reviewlink', 'Link om een review te schrijven', 'https://g.page/r/beautybewust/review')
const boeklink = v('boeklink', 'Link om een afspraak te maken', 'https://beautybewust.nl/behandelingen#boeken')
const sessies = v('sessies', 'Sessies die nog over zijn, bijv. "2 sessies"', '2 sessies')
const kuurgeldigtot = v('geldigtot', 'Laatste dag dat de kuur geldig is', '3 oktober 2028')

const b = (id: string, body: string, heading = ''): MailTextBlock => ({ id, heading, body })

type Seed = Omit<MailTemplate, 'standard'>
/** De standaardtekst is wat Laravel kent; bij het opbouwen gelijk aan wat er nu staat. */
const withStandard = <T extends Seed | Omit<MailSeries, 'standard'>>(m: T) => ({
  ...m,
  standard: { subject: m.subject, preheader: m.preheader, blocks: m.blocks.map((x) => ({ ...x })) },
})

const templates: Seed[] = [
  {
    key: 'order-received',
    group: 'bestelling',
    name: 'Bestelling ontvangen',
    trigger: 'Direct na betalen',
    subject: 'Dankjewel voor je bestelling, {voornaam}',
    preheader: 'Bestelling {bestelnummer} is binnen. Ik pak hem met zorg voor je in.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nWat fijn dat je voor Beauty Bewust kiest. Je bestelling {bestelnummer} is binnen en betaald. Ik pak hem binnen twee werkdagen voor je in.\n\nVragen over gebruik? Antwoord gewoon op deze mail.')],
    enabled: true,
    required: true,
    variables: [voornaam, bestelnummer, bestellink],
    button: { label: 'Bekijk je bestelling', variable: 'bestellink' },
  },
  {
    key: 'payment-failed',
    group: 'bestelling',
    name: 'Betaling niet gelukt',
    trigger: 'Als de betaling mislukt of verloopt',
    subject: 'Je betaling is niet gelukt',
    preheader: 'Je bestelling {bestelnummer} staat nog voor je klaar.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nDe betaling van je bestelling is niet gelukt. Er is niets afgeschreven. Je mand staat nog klaar, dus je kunt het zo opnieuw proberen.')],
    enabled: true,
    required: true,
    variables: [voornaam, bestelnummer, bestellink],
    button: { label: 'Opnieuw betalen', variable: 'bestellink' },
  },
  {
    key: 'order-refunded',
    group: 'bestelling',
    name: 'Terugbetaling',
    trigger: 'Als Sarah (een deel) terugbetaalt',
    subject: 'Je terugbetaling van {bedrag} is onderweg',
    preheader: 'Binnen een paar werkdagen op je rekening.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nIk heb {bedrag} van bestelling {bestelnummer} teruggestort. Het staat binnen een paar werkdagen op je rekening.')],
    enabled: true,
    required: true,
    variables: [voornaam, bestelnummer, bedrag],
  },
  {
    key: 'order-shipped',
    group: 'verzending',
    name: 'Pakket onderweg',
    trigger: 'Als Sarah de bestelling op Verzonden zet',
    subject: 'Je pakket is onderweg, {voornaam}',
    preheader: 'Met track & trace, zodat je weet wanneer hij komt.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nJe bestelling {bestelnummer} is onderweg. Met de knop hieronder zie je waar je pakket is.')],
    enabled: true,
    required: false,
    variables: [voornaam, bestelnummer, volglink],
    button: { label: 'Volg je pakket', variable: 'volglink' },
  },
  {
    key: 'order-delivered',
    group: 'verzending',
    name: 'Bezorgd: zo gebruik je het',
    trigger: 'Een dag na bezorging',
    subject: 'Zo haal je het meeste uit je nieuwe producten',
    preheader: 'Drie tips van Sarah voor de eerste weken.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nJe pakket is bezorgd. Begin rustig: nieuwe producten één voor één, en geef je huid twee weken om te wennen.')],
    enabled: true,
    required: false,
    variables: [voornaam, bestellink],
  },
  {
    key: 'appointment-requested',
    group: 'afspraken',
    name: 'Aanvraag ontvangen',
    trigger: 'Direct na een afspraakaanvraag',
    subject: 'Je aanvraag voor {datum} is binnen',
    preheader: 'Ik bevestig hem zo snel mogelijk.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nDank je voor je aanvraag voor {behandeling} bij {behandelaar} op {datum} om {tijd}. Ik kijk er vandaag nog naar en bevestig hem per mail.')],
    enabled: true,
    required: true,
    variables: [voornaam, behandeling, behandelaar, datum, tijd],
  },
  {
    key: 'appointment-confirmed',
    group: 'afspraken',
    name: 'Afspraak bevestigd',
    trigger: 'Als Sarah de afspraak bevestigt',
    subject: 'Tot {datum}, {voornaam}',
    preheader: '{behandeling} om {tijd}. Verzetten kan tot 24 uur van tevoren.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nJe afspraak staat: {behandeling} bij {behandelaar} op {datum} om {tijd}. Kom met een schone huid, zonder make-up als dat kan.\n\nToch niet gelegen? Verzet of annuleer hem tot 24 uur van tevoren.')],
    enabled: true,
    required: true,
    variables: [voornaam, behandeling, behandelaar, datum, tijd, afspraaklink],
    button: { label: 'Bekijk je afspraak', variable: 'afspraaklink' },
  },
  {
    key: 'appointment-reminder',
    group: 'afspraken',
    name: 'Herinnering',
    trigger: 'Een dag voor de afspraak',
    subject: 'Morgen om {tijd} zie ik je graag',
    preheader: '{behandeling} op {datum}.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nEen klein seintje: morgen om {tijd} heb je {behandeling} bij {behandelaar}. Tot dan!')],
    enabled: true,
    required: false,
    variables: [voornaam, behandeling, behandelaar, datum, tijd, afspraaklink],
  },
  {
    key: 'appointment-moved',
    group: 'afspraken',
    name: 'Afspraak verzet',
    trigger: 'Als de salon een afspraak verzet of een andere tijd voorstelt',
    subject: 'Je afspraak is verzet naar {datum}',
    preheader: '{behandeling} om {tijd} bij {behandelaar}.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nJe afspraak voor {behandeling} staat nu op {datum} om {tijd}, bij {behandelaar}. Past dit niet? Kies hieronder zelf een andere tijd.')],
    enabled: true,
    required: true,
    variables: [voornaam, behandeling, behandelaar, datum, tijd, afspraaklink],
    button: { label: 'Bekijk je afspraak', variable: 'afspraaklink' },
  },
  {
    key: 'appointment-performer-absent',
    group: 'afspraken',
    name: 'Afspraak gaat niet door (afwezig)',
    trigger: 'Als Sarah bij "Afwezig" kiest voor een bericht aan de klant',
    subject: 'Je afspraak op {datum} gaat helaas niet door',
    preheader: 'Kies een nieuwe tijd die jou past.',
    blocks: [
      b(
        'b1',
        'Hoi {voornaam},\n\n{behandelaar} kan op {datum} helaas niet werken, dus je afspraak voor {behandeling} om {tijd} gaat niet door. Het spijt me.\n\nKies hieronder een nieuwe tijd. Gaat het om een kuursessie, dan telt die gewoon niet mee.',
      ),
    ],
    enabled: true,
    required: true,
    variables: [voornaam, behandeling, behandelaar, datum, tijd, afspraaklink],
    button: { label: 'Kies een nieuwe tijd', variable: 'afspraaklink' },
  },
  {
    key: 'kuur-expiring',
    group: 'afspraken',
    name: 'Kuur verloopt bijna',
    trigger: '30 dagen voor een vooruitbetaalde kuur verloopt, als er nog sessies over zijn (één keer per kuur)',
    subject: 'Je kuur is nog geldig tot {geldigtot}',
    preheader: 'Plan je laatste sessies, dan gaat er niets verloren.',
    blocks: [
      b(
        'b1',
        'Hoi {voornaam},\n\nJe hebt nog {sessies} van je {behandeling}-kuur. Hij is al betaald en geldig tot {geldigtot}. Daarna vervalt wat er over is.\n\nPlan ze hieronder in; past het echt niet, antwoord dan op deze mail.',
      ),
    ],
    enabled: true,
    required: false,
    variables: [voornaam, behandeling, sessies, kuurgeldigtot, afspraaklink],
    button: { label: 'Plan je sessies', variable: 'afspraaklink' },
  },
  {
    key: 'giftcard-sent',
    group: 'cadeaubon',
    name: 'Cadeaubon',
    trigger: 'Na betalen van een cadeaubon',
    subject: 'Je cadeaubon van {bedrag}',
    preheader: 'Met de code en tot wanneer hij geldig is.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nHier is je cadeaubon van {bedrag}. De code is {code}, geldig tot {geldigtot}. Te gebruiken in de webshop en in de salon.')],
    enabled: true,
    required: true,
    variables: [voornaam, bedrag, code, geldigtot],
  },
  {
    key: 'subscription-upcoming',
    group: 'abonnement',
    name: 'Levering komt eraan',
    trigger: 'Drie dagen voor een abonnementslevering',
    subject: 'Op {leverdatum} komt je {product}',
    preheader: 'Nog iets wijzigen? Dat kan tot morgen.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nOp {leverdatum} stuur ik je volgende {product}. Wil je iets overslaan of uitstellen? Dat kan tot morgen in Mijn omgeving.')],
    enabled: true,
    required: false,
    variables: [voornaam, product, leverdatum, abonnementlink],
    button: { label: 'Naar mijn abonnement', variable: 'abonnementlink' },
  },
  {
    key: 'subscription-payment-failed',
    group: 'abonnement',
    name: 'Incasso mislukt',
    trigger: 'Als een abonnementsbetaling mislukt',
    subject: 'Je abonnementsbetaling is niet gelukt',
    preheader: 'Werk je betaalgegevens bij, dan stuur ik je levering alsnog.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nDe betaling voor je volgende {product} is niet gelukt. Werk je betaalgegevens bij, dan gaat je levering alsnog mee.')],
    enabled: true,
    required: true,
    variables: [voornaam, product, abonnementlink],
    button: { label: 'Betaalgegevens bijwerken', variable: 'abonnementlink' },
  },
  {
    key: 'magic-link',
    group: 'account',
    name: 'Inloglink',
    trigger: 'Als iemand een inloglink vraagt',
    subject: 'Je inloglink voor Beauty Bewust',
    preheader: '15 minuten geldig.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nKlik op de knop om in te loggen. De link werkt 15 minuten en één keer. Vroeg je hem niet aan? Dan kun je deze mail negeren.')],
    enabled: true,
    required: true,
    variables: [voornaam, inloglink],
    button: { label: 'Inloggen', variable: 'inloglink' },
  },
  {
    key: 'password-reset',
    group: 'account',
    name: 'Wachtwoord herstellen',
    trigger: 'Als iemand een nieuw wachtwoord vraagt',
    subject: 'Kies een nieuw wachtwoord',
    preheader: 'De link werkt een uur.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nMet de knop hieronder kies je een nieuw wachtwoord. Vroeg je dit niet aan? Dan blijft je oude wachtwoord gewoon werken.')],
    enabled: true,
    required: true,
    variables: [voornaam, herstellink],
    button: { label: 'Nieuw wachtwoord kiezen', variable: 'herstellink' },
  },
]

const series: Omit<MailSeries, 'standard'>[] = [
  {
    key: 'welkom',
    name: 'Welkom',
    trigger: 'Na het bevestigen van de nieuwsbrief',
    enabled: true,
    wait: 0,
    waitUnit: 'dagen',
    subject: 'Welkom, {voornaam}. Fijn dat je er bent',
    preheader: 'Wie ik ben en wat je van me kunt verwachten.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nIk ben Sarah. Eén keer per maand schrijf ik over huid en seizoen, zonder schreeuwerige aanbiedingen. Twijfel je wat bij jouw huid past? Doe het huidadvies.')],
    variables: [voornaam, winkellink],
    button: { label: 'Naar de webshop', variable: 'winkellink' },
    audience: 'Iedereen die de nieuwsbrief bevestigt',
  },
  {
    key: 'bijna-op',
    name: 'Bijna op',
    trigger: 'Als een product volgens de gebruiksduur bijna op is',
    enabled: true,
    wait: 7,
    waitUnit: 'dagen',
    subject: 'Je {product} is bijna op',
    preheader: 'Op tijd bijbestellen, zodat je routine doorloopt.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nAls je hem dagelijks gebruikt, is je {product} over ongeveer een week op. Zal ik er een nieuwe voor je klaarzetten?')],
    variables: [voornaam, product, winkellink],
    button: { label: 'Bijbestellen', variable: 'winkellink' },
    audience: 'Klanten zonder abonnement op dat product, met seintje per mail aan',
  },
  {
    key: 'review',
    name: 'Reviewverzoek',
    trigger: 'Na een afgeronde behandeling',
    enabled: true,
    wait: 2,
    waitUnit: 'dagen',
    subject: 'Hoe voelt je huid nu, {voornaam}?',
    preheader: 'Je mening helpt anderen die twijfelen.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nDank je voor je bezoek. Hoe voelt je huid nu? Wil je in een paar zinnen vertellen hoe je het vond? Dat helpt anderen enorm.')],
    variables: [voornaam, behandeling, reviewlink],
    button: { label: 'Schrijf een review', variable: 'reviewlink' },
    audience: 'Klanten na een afgeronde behandeling, één keer per 6 maanden',
  },
  {
    key: 'verjaardag',
    name: 'Verjaardag',
    trigger: 'Op de verjaardag',
    enabled: false,
    wait: 0,
    waitUnit: 'dagen',
    subject: 'Gefeliciteerd, {voornaam}!',
    preheader: 'Een klein cadeautje van mij.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nGefeliciteerd met je verjaardag! Bij je volgende bestelling deze maand doe ik er een proefje bij.')],
    variables: [voornaam, winkellink],
    audience: 'Alleen wie een verjaardag invulde in Mijn gegevens',
  },
  {
    key: 'lang-niet-gezien',
    name: 'Lang niet gezien',
    trigger: 'Als iemand lang niet in de salon was',
    enabled: false,
    wait: 120,
    waitUnit: 'dagen',
    subject: 'Ik mis je in de salon, {voornaam}',
    preheader: 'Even je huid laten checken?',
    blocks: [b('b1', 'Hoi {voornaam},\n\nHet is alweer even geleden. Je huid verandert met de seizoenen; zin in een nieuwe huidanalyse?')],
    variables: [voornaam, boeklink],
    button: { label: 'Maak een afspraak', variable: 'boeklink' },
    audience: 'Klanten met minstens één bezoek, wachttijd na het laatste bezoek',
  },
  {
    key: 'verlaten-mand',
    name: 'Verlaten winkelmand',
    trigger: 'Als er iets in de mand blijft liggen',
    enabled: true,
    wait: 4,
    waitUnit: 'uren',
    subject: 'Je mand staat nog voor je klaar',
    preheader: 'Twijfel je nog? Vraag het gerust.',
    blocks: [b('b1', 'Hoi {voornaam},\n\nJe hebt nog iets in je winkelmand liggen. Twijfel je of het bij je huid past? Antwoord op deze mail, dan denk ik met je mee.')],
    variables: [voornaam, mandlink],
    button: { label: 'Naar mijn mand', variable: 'mandlink' },
    audience: 'Alleen ingelogde klanten met nieuwsbrieftoestemming; hooguit één keer per week',
  },
]

export const seedMailTemplates: MailTemplate[] = templates.map(withStandard)
export const seedMailSeries: MailSeries[] = series.map(withStandard)

export const seedMailBrand: MailBrand = {
  senderName: 'Sarah van Beauty Bewust',
  replyTo: 'hallo@beautybewust.nl',
  logo: '',
  palette: 'zand',
  footer: {
    address: 'Beauty Bewust · Hoofdstraat 12 · 1234 AB Amsterdam',
    instagram: 'https://instagram.com/beautybewust',
    facebook: '',
    note: 'Vragen? Antwoord gewoon op deze mail, ik lees alles zelf.',
  },
}

// ——— Abonnees ———

const names = ['Eva', 'Lotte', 'Noor', 'Nadia', 'Sanne', 'Iris', 'Fleur', 'Maud', 'Julia', 'Anouk', 'Sophie', 'Lieke', 'Esmee', 'Romy', 'Femke', 'Yara', 'Merel', 'Isa', 'Zoë', 'Lara']
const domains = ['gmail.com', 'hotmail.com', 'outlook.com', 'ziggo.nl', 'icloud.com']
const sources: Subscriber['source'][] = ['footer', 'footer', 'account', 'footer', 'quiz']

/** Een vast patroon (geen Math.random), zodat de demo bij elke opbouw hetzelfde toont. */
export function buildSeedSubscribers(today: string, shift: (date: string, days: number) => string): Subscriber[] {
  return Array.from({ length: 48 }, (_, i) => {
    const first = names[i % names.length]
    const days = 3 + ((i * 37) % 170)
    const subscribedAt = shift(today, -days)
    const status: Subscriber['status'] = i % 17 === 5 ? 'afgemeld' : i % 19 === 7 ? 'onbestelbaar' : i % 11 === 3 && days < 20 ? 'onbevestigd' : 'bevestigd'
    return {
      id: `sub-${i + 1}`,
      email: `${first.toLowerCase().replace('ë', 'e')}${i > 19 ? i : ''}@${domains[i % domains.length]}`,
      firstName: i % 4 === 3 ? null : first,
      source: sources[i % sources.length],
      subscribedAt,
      confirmedAt: status === 'onbevestigd' ? null : shift(subscribedAt, i % 3 === 0 ? 1 : 0),
      status,
      customerId: i < 6 ? ['cust-eva', 'cust-lotte', 'cust-noor', 'cust-nadia', null, null][i] : null,
    }
  })
}

/** Doelgroepen; het aantal rekent de demo-server uit (zie mailBackend). */
export const seedSegments: Omit<Segment, 'count'>[] = [
  { id: 'seg-klanten', name: 'Klanten', rules: [{ kind: 'customer', value: true }] },
  { id: 'seg-nog-geen-klant', name: 'Nog geen klant', rules: [{ kind: 'customer', value: false }] },
  { id: 'seg-gevoelig', name: 'Gevoelige huid', rules: [{ kind: 'skinType', skinType: 'Gevoelige huid' }] },
  { id: 'seg-lang-niet', name: 'Lang niet gezien (12+ weken)', rules: [{ kind: 'customer', value: true }, { kind: 'lastVisitWeeks', weeks: 12 }] },
]
